import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal as AntModal } from 'antd';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import { CalendarWorkflowErrorLogs } from '../CalendarWorkflowErrorLogs';
import { CalendarEditForm } from '@/components/CalendarSetup/CalendarEditForm';
import { GhlConnectionStatus } from './GhlConnectionStatus';
import { CalendarActionButtons } from './CalendarActionButtons';
import { CalendarWorkflowActionsProps } from '@/interfaces/ICalendarWorkflows';
import { createCalendarActionHandlers } from '../handlers/CalendarHandlers';

export const CalendarWorkflowActions: React.FC<CalendarWorkflowActionsProps> = ({
	calendar,
	calendarWorkflows,
	onRefetch,
	onRefetchCalendars,
}) => {
	const dispatch = useAppDispatch();
	const selectedAgent = useAppSelector(getSelectedAgent);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showErrorLogsModal, setShowErrorLogsModal] = useState(false);

	const { handleCalendarUpdate, handleDeleteCalendar } = createCalendarActionHandlers(
		calendar,
		calendarWorkflows,
		selectedAgent,
		selectedAgentId,
		dispatch,
		onRefetch,
		setIsDeleting,
		setShowEditModal,
		onRefetchCalendars,
	);

	const handleEditCalendar = () => {
		setShowEditModal(true);
	};

	const showErrorLogs = () => {
		setShowErrorLogsModal(true);
	};

	const workFlowForErrorLogs = {
		workflowName: calendar.eventTypeId,
		forwardedFrom: selectedAgent?.number,
	};

	return (
		<>
			<div className="flex items-center justify-between w-full">
				<GhlConnectionStatus calendar={calendar} />

				<CalendarActionButtons
					onEdit={handleEditCalendar}
					onShowErrorLogs={showErrorLogs}
					onDelete={handleDeleteCalendar}
					isDeleting={isDeleting}
				/>
			</div>

			<AntModal
				title="Edit Calendar Configuration"
				open={showEditModal}
				onCancel={() => setShowEditModal(false)}
				footer={null}
				width={600}
			>
				<CalendarEditForm
					initialValues={{ data: { calendars: [calendar] } }}
					onFinish={handleCalendarUpdate}
					onCancel={() => setShowEditModal(false)}
				/>
			</AntModal>

			<AntModal
				title={
					<div className="flex items-center gap-2">
						<AlertTriangle size={20} className="text-orange-600" />
						<span className="text-xl font-semibold text-gray-800">Error Logs</span>
					</div>
				}
				open={showErrorLogsModal}
				onCancel={() => setShowErrorLogsModal(false)}
				footer={null}
				width="50%"
			>
				<CalendarWorkflowErrorLogs workFlowForErrorLogs={workFlowForErrorLogs} />
			</AntModal>
		</>
	);
};
